import { useState, useEffect, createContext, useRef } from "react";
import { ConsoleLog } from "@inntechcorp/it-design";

import { GetLocalState } from "Connectivity/LocalStorage";
import { PublicSocketContextProps, PublicSocketJackpotProps } from "Types/PublicSocketContextProps";
import { GameLiveDataProps } from "Types/GameProps";

type GameDataListeners = {
  [gameId: string]: ((data: GameLiveDataProps) => void)[];
}

const PublicSocketContext = createContext<PublicSocketContextProps>({} as PublicSocketContextProps);
      PublicSocketContext.displayName = "PublicSocketContext";

export const PublicSocketProvider = ({ children }:{children:any}) => {

  const [jackpot, SetJackpot] = useState<PublicSocketJackpotProps>({
    creditToUser      : "0.00",
    currencyCode      : "EUR",
    debitFromUser     : "0.00",
    duration          : "00:00:00",
    gameCount         : 0,
    startDate         : new Date()
  });

  const [wsURL,       SetWsURL]       = useState("");
  const [name,        SetName]        = useState("");
  const [room,        SetRoom]        = useState("");
  const [connection,  SetConnection]  = useState<any | null>(null);
  
  /**
   * 0 - not connected
   * 1 - connecting
   * 2 - connected
   * 3 - was reconnected
   * 4 - disconnected
   * 5 - socket error
   */
  const [status, SetStatus] = useState(0);

  const gameListenersRef = useRef<GameDataListeners>({});

  useEffect(() => {
    if ( room !== "" ) {
      listen();
    }
  }, [room]);

  const listenPublicSocket = (
    wsURL = GetLocalState("settings").socket.Path,
    name  = GetLocalState("settings").socket.Namespace, 
    room  = "test-client_public"
  ) => {

    if ( window.location.hostname === "localhost" ) {
      wsURL   = wsURL.replace("[[SITE_URL]]", "staging-site.inntech.ae");
      room    = "test-client_public";
    }
    else {
      wsURL   = wsURL.replace("[[SITE_URL]]", window.location.hostname);
      room    = GetLocalState("settings").client.slug + GetLocalState("settings").socket.PublicRoom;
    }

    if(import.meta.env.MODE !== "development") {
      wsURL = "/ws";
    }
    
    SetWsURL(wsURL);
    SetName(name);
    SetRoom(room);

  };

  const cancelPublicSocket = () => {

    reset();
    SetStatus(0);

  };

  const reset = () => {  

    if ( connection )
      connection.close();

    SetWsURL("");
    SetName("");
    SetConnection(null);

  }

  /**
   * ----------------------------------------
   * 
   * ----------------------------------------
   */

  //const handleConnect = (nsConn:any, msg:any) => {
  const handleConnect = () => {
    
    ConsoleLog.single("🛰️ [Public Socket] Connecting...");

  }

  //const handleConnected = (nsConn:any, msg:any) => {
  const handleConnected = (nsConn:any) => {

    nsConn.joinRoom(room);

    if(nsConn.conn.wasReconnected()) {
      SetStatus(3);
      ConsoleLog.single("🛰️ [Public Socket] Reconnected");
    }
    else {
      SetStatus(2);
      ConsoleLog.single("🛰️ [Public Socket] Connected");
    }
    
  }

  //const handleDisconnect = (nsConn:any, msg:any) => {
  const handleDisconnect = () => {
    
    ConsoleLog.single("🛰️ [Public Socket] Disconnected");

    SetStatus(4);
    // try reconnect
    //listen();
  }

  //const handleRoomJoin = (nsConn:any, msg:any) => {
  const handleRoomJoin = () => {
    ConsoleLog.single("🛰️ [Public Socket] Joining Room...");
  }

  //const handlRoomJoined = (nsConn:any, msg:any) => {
  const handlRoomJoined = () => {
    ConsoleLog.single("🛰️ [Public Socket] Successfully Joined The Room > " + room);
  }
  
  //const handleRoomLeft = (nsConn:any, msg:any) => {
  const handleRoomLeft = () => {
    ConsoleLog.single("🛰️ [Public Socket] Room Left");
  }

  //const handleAnyEvent = (nsConn:any, msg:any) => {
  const handleAnyEvent = () => {
    // console.log(msg)
  }

  //const handleStream = (nsConn, msg:any) => {
  const handleStream = () => {
    // console.log(msg.Body);
    // console.log(msg)
  }

  const handleJackpot = (nsConn:any, msg:any) => {
    nsConn;
    ConsoleLog.single("🛰️ [Public Socket] Jackpot Updated");
    SetJackpot(JSON.parse(msg.Body) as PublicSocketJackpotProps);
  }

  const handleLiveData = (nsConn:any, msg:any) => {
    
    nsConn;
    const newData:GameLiveDataProps = JSON.parse(msg.Body);

    if (gameListenersRef.current[newData.id]) {
      gameListenersRef.current[newData.id].forEach(callback => callback(newData));
    }

  }

  const subscribeToGame = (gameId: string, callback: (data: GameLiveDataProps) => void) => {
    
    const listeners = gameListenersRef.current[gameId] || [];
    
    gameListenersRef.current = {
      ...gameListenersRef.current,
      [gameId]: [...listeners, callback]
    };

    return () => {
      const currentListeners = gameListenersRef.current[gameId] || [];
      gameListenersRef.current = {
        ...gameListenersRef.current,
        [gameId]: currentListeners.filter(cb => cb !== callback)
      };
    };
  };

  async function listen() {
    
    let conn;

    SetStatus(1);
    ConsoleLog.single("🛰️ [Public Socket] Listening the socket...");
    
    try {

      conn = await (window as any).neffos.dial(wsURL, {

        site: {

          _OnNamespaceConnect     : handleConnect,
          _OnNamespaceConnected   : handleConnected,
          _OnNamespaceDisconnect  : handleDisconnect,
          _OnRoomJoin             : handleRoomJoin,
          _OnRoomJoined           : handlRoomJoined,
          _OnRoomLeft             : handleRoomLeft,
          _OnAnyEvent             : handleAnyEvent,
          live_data_update        : handleLiveData,
          jackpot_update          : handleJackpot,
          stream                  : handleStream

        }

      },
      {
        reconnect : 500,
        headers: {
          "X-Client-Slug" : GetLocalState("settings").client.slug
        }
      });
      
      conn.connect(name);
      SetConnection(conn);

    } 
    catch (err) {
      
      SetStatus(5);
      reset();
      ConsoleLog.double("🔴 Inn.tech Web Error [Public Socket Context]", err, "error");

    }

  }

  const getContextValue:PublicSocketContextProps = {
    jackpot,
    status,
    subscribeToGame,
    listenPublicSocket,
    cancelPublicSocket
  };

  return (

    <PublicSocketContext.Provider
      value={getContextValue}
    >
      {children}
    </PublicSocketContext.Provider>
    
  );

};

export default PublicSocketContext;